import { useState, useContext } from 'react';
import {
  ConfigProvider,
  Divider,
  Flex,
  Modal,
  Typography,
  Button,
  Form,
  Input,
  InputNumber,
} from 'antd';
import Icon from '@ant-design/icons';
import { useTheme } from '../../../context/ThemeContext';
import { ReactComponent as BroadcastSvg } from '../../../assets/icons/broadcast.svg';
import { ReactComponent as BroadcastBlackSvg } from '../../../assets/icons/broadcast-black.svg';
import { ReactComponent as CancelSvg } from '../../../assets/icons/cancel.svg';
import { ReactComponent as CancelBlackSvg } from '../../../assets/icons/cancel-black.svg';
import '../Broadcast.css';
import { API } from '../../../services';
import { ErrorMessageContext } from '../../../context/ErrorMessageContext';
import { DARK } from '../../../utilities/constants';
import textAreaCharacterCount from '../../../components/TextAreaCharacterCount/TextAreaCharacterCount';
import {
  sanitizeInput,
  getErrorMessage,
} from '../../../utilities/commonFunctions';
import PropTypes from 'prop-types';

const { Text, Title } = Typography;
const { TextArea } = Input;

const BroadcastModal = ({ isModalOpen, setIsModalOpen, tripDetails }) => {
  const { theme } = useTheme();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { addErrorMessage } = useContext(ErrorMessageContext);

  const onFinish = async values => {
    const { message, repeatCount } = values;
    try {
      setLoading(true);
      const payload = {
        targets: [
          {
            device_id: tripDetails.ridebuddy_deviceid,
            trip_id: tripDetails?.trip_id,
          },
        ],
        triggerfrequency: repeatCount,
        text_message: sanitizeInput(message),
        sent_by: sessionStorage.getItem('email'),
      };
      const { data } = await API.postBroadcastMessage(payload);
      addErrorMessage(data?.message, 'success');
      form.resetFields();
      setIsModalOpen(false);
    } catch (error) {
      addErrorMessage(
        getErrorMessage(
          error,
          'Unable to broadcast the message! Please try again after sometime.'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setIsModalOpen(false);
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Typography: {
            titleMarginTop: 0,
            fontSize: '1rem',
          },
          Form: {
            itemMarginBottom: 0,
          },
          Input: {
            colorBgContainer: 'var(--broadcast_form-input-bg-color, #2E3033)',
            paddingBlock: '0.75rem',
          },
          InputNumber: {
            colorBgContainer: 'var(--broadcast_form-input-bg-color, #2E3033)',
            fontSize: '1rem',
            colorBorder: 'transparent',
            colorTextPlaceholder: 'var(--placeholder-text-color, #ffffff80)',
            paddingBlock: '0.75rem',
          },
        },
        token: { colorSplit: '#656A6F' },
      }}
    >
      <Modal
        open={isModalOpen}
        centered
        destroyOnHidden
        title={
          <Flex vertical>
            <Flex className="broadcast-modal-title" justify="space-between">
              <Flex gap={8}>
                <Icon
                  component={theme === DARK ? BroadcastSvg : BroadcastBlackSvg}
                />
                <Text>Broadcast Message</Text>
              </Flex>
              <Icon
                component={theme === DARK ? CancelSvg : CancelBlackSvg}
                onClick={handleCancel}
              />
            </Flex>
            <Divider />
          </Flex>
        }
        footer={[
          <Button
            key="send"
            type="primary"
            htmlType="submit"
            loading={loading}
            onClick={() => form.submit()}
          >
            Send Message
          </Button>,
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
        ]}
        className="broadcast-modal"
        closable={false}
        width="40%"
      >
        <Flex vertical gap={12}>
          <Title
            level={4}
            style={{ marginTop: '0px' }}
          >{`${tripDetails?.trip_id} | ${tripDetails?.registration}`}</Title>
          <Divider />
          <Form
            form={form}
            onFinish={onFinish}
            layout="vertical"
            className="full-height broadcast-form"
            scrollToFirstError
            requiredMark={false}
          >
            <Form.Item
              name="message"
              className="message-form-item-modal"
              rules={[
                {
                  required: true,
                  message: 'Please enter message',
                },
              ]}
              label={
                <Text strong style={{ marginTop: '4px' }}>
                  Message
                </Text>
              }
            >
              <TextArea
                autoSize
                placeholder="Type your message here&#10;e.g: Hello from ridebuddy"
                rows={4}
                maxLength={500}
                count={{
                  max: 500,
                  show: textAreaCharacterCount,
                }}
              />
            </Form.Item>
            <Form.Item
              name="repeatCount"
              className="frequency-form-item-modal"
              rules={[
                {
                  required: true,
                  message: 'Please enter frequency',
                },
              ]}
              label={
                <Flex className="full-width" vertical gap={10}>
                  <Title level={5} style={{ marginTop: '0px' }}>
                    Define Frequency
                  </Title>
                </Flex>
              }
            >
              <InputNumber
                min={1}
                controls={false}
                variant="borderless"
                placeholder="number of times e.g.: 3"
                addonBefore={
                  <Text>
                    <b>Repeat message</b>
                  </Text>
                }
                addonAfter={
                  <Text>
                    <b>time(s)</b>
                  </Text>
                }
                type="number"
              />
            </Form.Item>
          </Form>
        </Flex>
      </Modal>
    </ConfigProvider>
  );
};

BroadcastModal.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  setIsModalOpen: PropTypes.func.isRequired,
  tripDetails: PropTypes.object,
};

export default BroadcastModal;
